import { Button, Modal, notification, type TableColumnsType, Tag, theme, Typography } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import { CircleAlert, PlusCircle, Trash2 } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { type Key, type ReactNode, useReducer, useState } from 'react';
import { from, switchMap, throwError, of, tap, catchError, EMPTY } from 'rxjs';
import ProductionLineForm from './production-line.form';
import ProductionLinesFilter from './production-lines.filter';
import AuthorizedComponent from '../../../components/authorized-component';
import { DataTable } from '../../../components/data-table';
import SyncSuspense from '../../../components/sync-suspense';
import { ExtraContent } from '../../../contexts/extra-content-context-provider';
import { type MealCategory, ProductionLine } from '../../../entities';
import useDebouncedState from '../../../hooks/use-debounced-state';
import { useQuery } from '../../../jsonapi-provider';
import { Roles } from '../../../types';

// const ProductionLineFormLazy = React.lazy(() => import('./production-line.form'));

export interface ProductionLineFiltersProps {
  onChange?: (filters: FiltersState) => void;
  initialFilters: FiltersState;
}

interface FiltersState {
  query: string;
  mealCategory?: string[];
  hhcRoute?: number[];
}

interface ProductionLinePageState {
  filters: ProductionLineFiltersProps['initialFilters']; // TODO: To be replaced
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_SORT_FIELD = 'name';
const DEFAULT_SORT_DIRECTION: SortOrder = 'descend';
const SUCCESS_DELETE_MESSAGE = 'Tag deleted successfully';

const serializeSort = (sort: ProductionLinePageState['sort']): string | undefined => {
  if (sort.field && sort.order) {
    const field = Array.isArray(sort.field) ? sort.field.join(',') : sort.field.toString();
    return `${sort.order === 'descend' ? '-' : ''}${field}`;
  }
  return undefined;
};

const serializeFilters = (filters: ProductionLinePageState['filters']): string | undefined => {
  const parts: string[] = [];
  if (filters?.query) parts.push(`name=ini='*${filters.query}*'`);
  if (filters?.mealCategory?.length) {
    parts.push(`mealCategories.id=in=(${filters?.mealCategory.map((id) => `${id}`).join(',')})`);
  }
  if (filters?.hhcRoute?.length) {
    parts.push(
      `hhcRoutesNumbers=supersetof=(${filters?.hhcRoute.map((id) => `'${id}'`).join(',')})`
    );
  }

  return parts.length ? parts.join(';') : undefined;
};

const reducer = (
  state: ProductionLinePageState,
  action: { type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT'; payload?: any }
): ProductionLinePageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: { query: '' } };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload,
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload,
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as ProductionLinePageState['sort'],
      };
  }
};
const ProductionLinesPage = (): ReactNode => {
  const { token } = theme.useToken();
  const [selectedRowKey, setSelectedRowKey] = useState<string>();
  const [productionLineToEdit, setProductionLineToEdit] = useState<ProductionLine | null>(null);
  const [state, setState] = useReducer(reducer, {
    filters: { query: '' },
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [debouncedState] = useDebouncedState({ state, time: 1000 });
  const productionLines = useQuery<ProductionLine[]>(
    debouncedState && [
      'production-line',
      {
        filter: serializeFilters(debouncedState.filters),
        page: {
          size: debouncedState.pagination.pageSize,
          number: debouncedState.pagination.current,
          totals: 1,
        },
        sort: serializeSort(debouncedState.sort),
        include: 'mealCategories',
        fields: {
          'production-line': 'name,mealCategories,hhcRoutesNumbers',
          'meal-category': 'name',
        },
      },
    ]
  );

  const columns: TableColumnsType<ProductionLine> = [
    { title: '', width: '50px' },
    {
      title: 'Name',
      dataIndex: ['name'],
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            setSelectedRowKey(item.id);
            setProductionLineToEdit(item);
          }}
        >
          {value}
        </Typography.Link>
      ),
    },
    {
      title: 'Meal Categories',
      dataIndex: ['mealCategories'],
      render: (value: MealCategory[]) =>
        value.map((mealCategory) => (
          <Tag key={mealCategory?.id} color="processing">
            {mealCategory?.name ?? '[DELETED MEAL CATEGORY]'}
          </Tag>
        )),
    },
    {
      title: 'Routes Numbers',
      dataIndex: ['hhcRoutesNumbers'],
      render: (value: string[]) =>
        value.map((hhcRoute) => (
          <Tag key={hhcRoute} color="processing">
            {hhcRoute}
          </Tag>
        )),
    },
  ];

  return (
    <>
      <DataTable<ProductionLine>
        title="Production Lines"
        description="List of production lines assembling meals"
        filtersNode={
          <ProductionLinesFilter
            initialFilters={state.filters}
            onChange={(filters) => {
              setState({ type: 'SET_FILTER', payload: filters });
            }}
          />
        }
        actionsNode={
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <Button
              type="primary"
              icon={<PlusCircle />}
              onClick={() => {
                setProductionLineToEdit(new ProductionLine());
              }}
            >
              Add Production Line
            </Button>
          </AuthorizedComponent>
        }
        selectedRowKeys={selectedRowKey ? [selectedRowKey] : undefined}
        dataList={productionLines}
        onChange={setState}
        currentPage={state.pagination.current}
        columns={columns}
        actions={[
          {
            key: 'delete',
            label: 'Delete',
            icon: <Trash2 />,
            danger: true,
            onItemClick: (record) => {
              Modal.confirm({
                width: '50%',
                title: 'Confirm deletion',
                icon: <CircleAlert />,
                content: (
                  <Typography.Text>
                    Are you sure you want to delete <b>{record.name}</b> permanently?
                  </Typography.Text>
                ),
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => {
                  from(productionLines.client.delete(['production-line', record.id]))
                    .pipe(
                      switchMap((res) =>
                        res.error
                          ? throwError(() => new Error(res.error?.detail ?? res.error?.title))
                          : of(res.data)
                      ),
                      tap(() => {
                        notification.success({
                          message: ' ',
                          description: SUCCESS_DELETE_MESSAGE,
                        });
                        productionLines.refetch?.();
                      }),
                      catchError((error: Error) => {
                        notification.error({
                          message: 'Failed to delete production line',
                          description: error.message,
                        });
                        return EMPTY;
                      })
                    )
                    .subscribe();
                },
              });
            },
          },
        ]}
      />
      <SyncSuspense condition={productionLineToEdit}>
        <ExtraContent>
          <ProductionLineForm
            productionLine={productionLineToEdit ?? new ProductionLine()}
            onSuccess={() => {
              productionLines.refetch?.();
              setProductionLineToEdit(null);
            }}
            onCancel={() => {
              setProductionLineToEdit(null);
            }}
          />
        </ExtraContent>
      </SyncSuspense>
    </>
  );
};

ProductionLinesPage.propTypes = {
  defaultPageNumber: PropTypes.number,
  defaultPageSize: PropTypes.number,
};

ProductionLinesPage.defaultProps = {
  defaultPageNumber: 1,
  defaultPageSize: 10,
  onVisibleColumnsChange: () => {},
  onSelectRowsChange: () => {},
};

ProductionLinesPage.displayName = 'ProductionLinesPage';
export default ProductionLinesPage;
